<template>
  <!-- Change one field on all tasks of a template, or on the tasks of one control objective (US 9398) -->
  <b-modal ref="dialog" modal-class="v2-modal" size="lg" hide-footer @hidden="reset">
    <template #modal-header>
      <div class="us-modal-head">
        <div>
          <h5 class="mb-0" qid="task-bulk-title">{{ $t('task_bulk.title') }}</h5>
          <p class="us-modal-head__sub">{{ $t('task_bulk.subtitle') }}</p>
        </div>
        <button type="button" class="us-icon-btn" :aria-label="$t('task_dialog.close')" @click="close">
          <font-awesome-icon icon="times"/>
        </button>
      </div>
    </template>

    <div class="us-form tb">
      <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>

      <div>
        <label class="us-field-label" for="tb-field">{{ $t('task_bulk.field') }}</label>
        <select id="tb-field" v-model="field" class="custom-select" qid="task-bulk-field" @change="value = null">
          <option :value="null" disabled>{{ $t('task_bulk.choose_field') }}</option>
          <optgroup v-for="group in fieldGroups" :key="group.key" :label="group.label">
            <option v-for="option in group.fields" :key="option.key" :value="option.key">{{ $t(option.label) }}</option>
          </optgroup>
        </select>
      </div>

      <div v-if="current">
        <label class="us-field-label">{{ $t('task_bulk.value') }}</label>
        <div v-if="current.useDefault" class="op-source__choices">
          <label v-for="choice in ['dropdowns', 'all']"
                 :key="choice"
                 class="op-source__option"
                 :class="{ 'is-on': value === choice }"
                 :qid="'task-bulk-default-' + choice"
          >
            <input type="radio" name="tb-default" :value="choice" v-model="value">
            <span><b>{{ $t('task_bulk.default.' + choice) }}</b><small>{{ $t('task_bulk.default.' + choice + '_text') }}</small></span>
          </label>
        </div>
        <TaskChoice v-else v-model="value" :choices="current.choices" qid="task-bulk-value"></TaskChoice>
      </div>

      <div v-if="objectives.length">
        <label class="us-field-label" for="tb-scope">{{ $t('task_bulk.scope') }}</label>
        <b-form-select id="tb-scope" v-model="objectiveId" :options="scopeOptions" qid="task-bulk-scope"></b-form-select>
      </div>

      <div v-if="current && value" class="us-note" :class="changes.length ? 'us-note--info' : ''" qid="task-bulk-preview">
        <font-awesome-icon icon="info-circle"/>
        <span>
          {{ $tc('task_bulk.preview', changes.length, { count: changes.length }) }}
          <template v-if="current.documentation"> {{ $t('task_bulk.documentation_only') }}</template>
          <template v-if="current.useDefault && value === 'all' && converted"> {{ $tc('task_bulk.converted', converted, { count: converted }) }}</template>
        </span>
      </div>

      <div class="us-modal-foot">
        <span class="us-grow"></span>
        <b-button variant="secondary" qid="task-bulk-cancel" @click="close">{{ $t('task_dialog.cancel') }}</b-button>
        <b-button variant="success" :disabled="!value || !changes.length || saving" qid="task-bulk-apply" @click="apply">
          <font-awesome-icon v-if="saving" icon="spinner" class="fa-spin mr-1"/>{{ $tc('task_bulk.apply', changes.length, { count: changes.length }) }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import TaskChoice from '@/components/assessment/TaskChoice.vue';

  const OFF_OPTIONAL_MANDATORY = ['No', 'Optional', 'Mandatory'];

  export default {
    name: 'TaskBulkDialog',
    components: {
      TaskChoice
    },
    props: {
      assessment: {
        type: Object,
        required: true
      },
      tasks: {
        type: Array,
        default: () => []
      },
      objectives: {
        type: Array,
        default: () => []
      },
      defaults: {
        type: Object,
        default: () => ({ response: [], documentation: [] })
      }
    },
    data() {
      return {
        field: null,
        value: null,
        objectiveId: null,
        error: '',
        saving: false
      }
    },
    computed: {
      // The fields, in the order of the task dialog; "use default" only when the template has one
      fields() {
        return [
          { group: 'task', key: 'add_response', label: 'task_dialog.response.title', choices: OFF_OPTIONAL_MANDATORY },
          { group: 'task', key: 'use_default_options', label: 'task_bulk.use_default', useDefault: true, hidden: !this.defaults.response.length },
          { group: 'task', key: 'add_control_description', label: 'task_dialog.fields.control_description', choices: OFF_OPTIONAL_MANDATORY },
          { group: 'task', key: 'add_performed_text', label: 'task_dialog.fields.performed_test', choices: OFF_OPTIONAL_MANDATORY },
          { group: 'task', key: 'add_description', label: 'task_dialog.fields.conclusion', choices: OFF_OPTIONAL_MANDATORY },
          { group: 'task', key: 'add_testing_notes', label: 'task_dialog.fields.testing_notes', choices: OFF_OPTIONAL_MANDATORY },
          { group: 'task', key: 'add_notes', label: 'task_dialog.fields.notes', choices: OFF_OPTIONAL_MANDATORY },
          { group: 'task', key: 'add_attachments', label: 'task_dialog.fields.attachments', choices: OFF_OPTIONAL_MANDATORY },
          { group: 'documentation', key: 'data_collection_add_description', label: 'task_dialog.documentation.comment', choices: ['Optional', 'Mandatory'], documentation: true },
          { group: 'documentation', key: 'data_collection_add_attachments', label: 'task_dialog.documentation.attachments', choices: OFF_OPTIONAL_MANDATORY, documentation: true },
          { group: 'documentation', key: 'add_response_doc', label: 'task_dialog.documentation.conclusion', choices: ['No', 'Optional', 'Mandatory', 'Auditor'], documentation: true },
          { group: 'documentation', key: 'use_default_options_doc', label: 'task_bulk.use_default', useDefault: true, documentation: true, hidden: !this.defaults.documentation.length }
        ].filter(field => !field.hidden);
      },
      fieldGroups() {
        return ['task', 'documentation'].map(group => ({
          key: group,
          label: this.$t(group === 'task' ? 'task_dialog.steps.task.title' : 'task_dialog.steps.documentation.title'),
          fields: this.fields.filter(field => field.group === group)
        }));
      },
      scopeOptions() {
        return [{ value: null, text: this.$t('task_bulk.all_tasks') }].concat(this.objectives.map((objective, index) => ({
          value: objective.id,
          text: this.$t('template_view.objective_name', { number: index + 1, name: objective.name })
        })));
      },
      current() {
        return _.find(this.fields, { key: this.field }) || null;
      },
      inScope() {
        return this.tasks.filter(task => (!this.objectiveId || task.control_objective_id === this.objectiveId)
          && (!this.current || !this.current.documentation || Number(task.data_collection_on) === 1));
      },
      // The tasks the change touches, worked out the same way as on the server
      changes() {
        if (!this.current || !this.value) {
          return [];
        }
        if (this.current.useDefault) {
          let doc = this.current.documentation;
          return this.inScope.filter(task => {
            let allow = doc ? task.add_response_doc : task.add_response;
            let type = doc ? task.response_type_doc : task.response_type;
            let linked = Number(doc ? task.use_default_options_doc : task.use_default_options) === 1;
            return allow !== 'No' && (this.value === 'all' || type === 'Dropdown') && !(linked && type === 'Dropdown');
          });
        }
        return this.inScope.filter(task => task[this.field] !== this.value);
      },
      // Yes / No and Date tasks that become drop-downs
      converted() {
        let doc = this.current && this.current.documentation;
        return this.changes.filter(task => (doc ? task.response_type_doc : task.response_type) !== 'Dropdown').length;
      }
    },
    methods: {
      open() {
        this.reset();
        this.$refs.dialog.show();
      },
      close() {
        this.$refs.dialog.hide();
      },
      reset() {
        this.field = null;
        this.value = null;
        this.objectiveId = null;
        this.error = '';
      },
      apply() {
        this.saving = true;
        this.error = '';
        piincHttp.put('assessments/' + this.assessment.slug + '/tasks', {
          field: this.field,
          value: this.value,
          control_objective_id: this.objectiveId || undefined,
          client: this.$store.state.client.slug
        }).then(response => {
          this.saving = false;
          this.$toastr('success', this.$tc('task_bulk.done', response.data.questions, { count: response.data.questions }));
          this.$emit('applied');
          this.close();
        }, error => {
          this.saving = false;
          this.error = error && error.data && error.data.message ? error.data.message : this.$t('users_page.error');
        });
      }
    }
  }
</script>
